import { useEffect, RefObject, MutableRefObject } from 'react';
import mapboxgl from 'mapbox-gl';
import { initializeMap } from '../MapInitializer';

export const useMapLifecycle = (
  mapContainer: RefObject<HTMLDivElement>,
  mapRef: MutableRefObject<mapboxgl.Map | null>,
  defaultCenter: [number, number],
  setIsLoading: (loading: boolean) => void,
  setError: (error: Error | null) => void,
  isVisible: boolean
) => {
  useEffect(() => {
    let isMounted = true;
    let initTimeout: NodeJS.Timeout;

    const initMap = async () => {
      try {
        if (!isMounted || !mapContainer.current || !isVisible) return;
        
        // If map already exists and is visible, don't reinitialize
        if (mapRef.current && isVisible) {
          console.log('Map already initialized and visible, skipping initialization');
          return;
        }

        // Clear existing map instance if it exists
        if (mapRef.current) {
          console.log('Removing existing map instance');
          mapRef.current.remove();
          mapRef.current = null;
        }

        console.log('Initializing map...');
        setIsLoading(true);
        
        const map = await initializeMap(mapContainer.current, defaultCenter);
        
        if (!map || !isMounted) {
          throw new Error('Failed to initialize map');
        }

        mapRef.current = map;
        setIsLoading(false);
        setError(null);
        
        console.log('Map initialized successfully');
      } catch (err) {
        console.error('Map initialization error:', err);
        if (!isMounted) return;

        const errorMessage = err instanceof Error ? err.message : 'Failed to initialize map';
        setError(new Error(errorMessage));
        setIsLoading(false);
      }
    };

    if (isVisible) {
      // Add a small delay before initialization to prevent rapid reloads
      initTimeout = setTimeout(() => {
        initMap();
      }, 300);
    } else {
      // If map becomes invisible, clean up the map instance
      if (mapRef.current) {
        console.log('Map no longer visible, cleaning up instance');
        mapRef.current.remove();
        mapRef.current = null;
      }
    }

    return () => {
      console.log('Cleaning up map lifecycle effect');
      isMounted = false;
      if (initTimeout) {
        clearTimeout(initTimeout);
      }
    };
  }, [mapContainer, mapRef, defaultCenter, setIsLoading, setError, isVisible]);
};